// Stand-in for the case details endpoint, built from the same cases as the list so the drawer
// always matches the row that opened it. Delete together with mock.data.js.
import dayjs from "dayjs"

import {
  CASE_REASON,
  CASE_VERDICT,
  EVIDENCE_FIELD,
  TIMELINE_EVENT
} from "../constants/case-details"
import { CASE_OUTCOME } from "../constants/outcomes"
import { CASES, mockResponse } from "./mock.data"

const REASON_CODE = { ce: "10.4", fpt: "4837" }

const STOPPED_VERDICT = { ce: CASE_VERDICT.BLOCKED, fpt: CASE_VERDICT.SHIFTED }

const NOT_STOPPED_VARIANTS = {
  ce: [
    { verdict: CASE_VERDICT.NOT_BLOCKED, reason: CASE_REASON.NO_MATCH },
    { verdict: CASE_VERDICT.NOT_BLOCKED, reason: CASE_REASON.NO_DEVICE },
    { verdict: CASE_VERDICT.TIMEOUT, reason: null },
    { verdict: CASE_VERDICT.REVERSED, reason: null }
  ],
  fpt: [
    { verdict: CASE_VERDICT.NOT_SHIFTED, reason: CASE_REASON.NO_MATCH },
    { verdict: CASE_VERDICT.NOT_ASSESSED, reason: null },
    { verdict: CASE_VERDICT.NOT_SHIFTED, reason: CASE_REASON.NO_DEVICE }
  ]
}

// The fields each network takes, in the order it lists them.
const EVIDENCE_FIELDS = {
  ce: [
    EVIDENCE_FIELD.IP_ADDRESS,
    EVIDENCE_FIELD.DEVICE_ID,
    EVIDENCE_FIELD.DEVICE_FINGERPRINT,
    EVIDENCE_FIELD.ACCOUNT_ID,
    EVIDENCE_FIELD.DELIVERY_ADDRESS
  ],
  fpt: [
    EVIDENCE_FIELD.IP_ADDRESS,
    EVIDENCE_FIELD.DEVICE_FINGERPRINT,
    EVIDENCE_FIELD.EMAIL,
    EVIDENCE_FIELD.PHONE,
    EVIDENCE_FIELD.SHIPPING_ADDRESS,
    EVIDENCE_FIELD.ACCOUNT_ID,
    EVIDENCE_FIELD.BILLING_ADDRESS
  ]
}

const DEVICE_FIELDS = [
  EVIDENCE_FIELD.IP_ADDRESS,
  EVIDENCE_FIELD.DEVICE_ID,
  EVIDENCE_FIELD.DEVICE_FINGERPRINT
]

const CAPTURED = {
  [EVIDENCE_FIELD.IP_ADDRESS]: () => true,
  [EVIDENCE_FIELD.DEVICE_ID]: (n) => n % 2 === 1,
  [EVIDENCE_FIELD.DEVICE_FINGERPRINT]: (n) => n % 3 === 0,
  [EVIDENCE_FIELD.ACCOUNT_ID]: (n) => n % 5 !== 2,
  [EVIDENCE_FIELD.DELIVERY_ADDRESS]: (n) => n % 4 !== 1,
  [EVIDENCE_FIELD.EMAIL]: () => true,
  [EVIDENCE_FIELD.PHONE]: (n) => n % 2 === 0,
  [EVIDENCE_FIELD.SHIPPING_ADDRESS]: (n) => n % 4 !== 1,
  [EVIDENCE_FIELD.BILLING_ADDRESS]: (n) => n % 3 !== 1
}

const PAST_AMOUNTS = [132, 99, 39, 588, 87, 59, 215, 64]

const decide = (c) => {
  if (c.outcome === CASE_OUTCOME.STOPPED)
    return { verdict: STOPPED_VERDICT[c.program], reason: null }
  if (c.outcome === CASE_OUTCOME.AWAITING) return { verdict: CASE_VERDICT.AWAITING, reason: null }
  // By position among the program's not-stopped cases, so every variant shows up at least once.
  const variants = NOT_STOPPED_VARIANTS[c.program]
  const index = CASES.filter(
    (x) => x.program === c.program && x.outcome === CASE_OUTCOME.NOT_STOPPED
  ).findIndex((x) => x.id === c.id)
  return variants[index % variants.length]
}

const pastOrderCount = (c, { verdict, reason }) => {
  if (reason === CASE_REASON.NO_DEVICE || verdict === CASE_VERDICT.NOT_ASSESSED) return 0
  if (reason === CASE_REASON.NO_MATCH) return c.id % 2
  return 2 + (c.id % 2)
}

const buildTimeline = (c, { verdict }, pastCount, reason) => {
  const t0 = dayjs(c.date)
  const t1 = t0.add(3, "minute")
  const t2 = t0.add(312, "minute")

  const received = { event: TIMELINE_EVENT.DISPUTE_RECEIVED, at: t0.toISOString() }

  const lookupEvent = () => {
    if (reason === CASE_REASON.NO_DEVICE) return TIMELINE_EVENT.PAST_ORDERS_NOT_REQUESTED
    if (verdict === CASE_VERDICT.NOT_ASSESSED) return TIMELINE_EVENT.DISPUTED_ORDER_CHECKED
    if (!pastCount) return TIMELINE_EVENT.NO_PAST_ORDERS
    return TIMELINE_EVENT.PAST_ORDERS_REQUESTED
  }
  const lookup = { event: lookupEvent(), at: t1.toISOString(), count: pastCount }

  if (verdict === CASE_VERDICT.AWAITING) {
    return [received, lookup, { event: TIMELINE_EVENT.AWAITING_DECISION, at: t1.toISOString() }]
  }

  if (verdict === CASE_VERDICT.REVERSED) {
    return [
      received,
      lookup,
      {
        event: TIMELINE_EVENT.DECISION,
        at: t2.toISOString(),
        verdict: CASE_VERDICT.BLOCKED,
        fee: null
      },
      { event: TIMELINE_EVENT.BLOCK_REVERSED, at: t2.add(26, "hour").toISOString(), fee: 0 }
    ]
  }

  return [
    received,
    lookup,
    { event: TIMELINE_EVENT.DECISION, at: t2.toISOString(), verdict, fee: c.fee_charged }
  ]
}

export const mockCase = ({ id }) => {
  const c = CASES.find((x) => x.id === Number(id))
  if (!c) return mockResponse(null)

  const decision = decide(c)
  const { reason } = decision
  const fields = EVIDENCE_FIELDS[c.program]

  const evidence = fields.map((field) => ({
    field,
    captured:
      !(reason === CASE_REASON.NO_DEVICE && DEVICE_FIELDS.includes(field)) && CAPTURED[field](c.id)
  }))
  const captured = evidence.filter((e) => e.captured).map((e) => e.field)

  const pastCount = pastOrderCount(c, decision)
  const past_orders = Array.from({ length: pastCount }, (_, i) => ({
    date: dayjs(c.date)
      .subtract(140 + i * 61, "day")
      .toISOString(),
    amount: PAST_AMOUNTS[(c.id + i) % PAST_AMOUNTS.length],
    matched_fields:
      reason === CASE_REASON.NO_MATCH && i > 0
        ? []
        : captured.filter((_, k) => (k + i + c.id) % 3 !== 2).slice(0, c.program === "ce" ? 3 : 4)
  }))

  return mockResponse({
    id: c.id,
    program: c.program,
    amount: c.amount,
    card: c.card,
    outcome: c.outcome,
    verdict: decision.verdict,
    reason,
    reason_code: REASON_CODE[c.program],
    evidence,
    past_orders,
    timeline: buildTimeline(c, decision, pastCount, reason)
  })
}
